import { Transform } from '../../engine/core/Transform';
import { Vec3 } from '../../engine/math/vec3';
import { AABB } from '../../engine/math/aabb';
import { Rng } from '../../engine/math/random';
import { cylinder, box, sphere, torusSegment, combine } from '../../engine/geo/primitives';
import { translate, setEmissive, rotateZ, rotateX, scale as scaleMesh } from '../../engine/geo/mesh';
import type { Ctx } from '../Ctx';
import type { Particles } from '../fx/Particles';
import { PALETTE, BIOMES, SPOTS } from '../world/Layout';
import { worldHeight } from '../world/Terrain';
import { instanced, jitterInstance, ironwoodTreeMesh, mushroomMesh, Instance } from '../world/Props';

/** Maze grid: cells of CELL metres; walls between cells are trunk clusters. */
export const MAZE = { n: 9, cell: 10, origin: new Vec3(200 - 45, 0, -45) }; // covers x∈[155,245], z∈[-45,45]

export interface IronwoodHandles {
  /** Cell centre in world space. */
  cellCenter(i: number, j: number): Vec3;
  /** Which cell contains a world point (may be outside the grid). */
  cellOf(p: Vec3): [number, number];
  /** Is there an open passage between adjacent cells? */
  open(i: number, j: number, i2: number, j2: number): boolean;
  /** Passages that are cut by the ravine; pathfinding treats them as closed until the plank bridges them. */
  blocked: Set<string>;
  /** BFS path of cell centres from a to b (inclusive). */
  path(a: [number, number], b: [number, number]): Vec3[];
  /** Waypoints the stag patrols (corner cells). */
  patrol: Vec3[];
  update(playerPos: Vec3, particles: Particles, dt: number): void;
  wellPos: Vec3;
}

/**
 * Ironwood (§2.4): a 9x9 cell maze whose walls are trunk clusters; three landmark clearings;
 * light shafts that lean toward the White Keep at the centre; fireflies; the well behind the keep.
 */
export function buildIronwood(ctx: Ctx): IronwoodHandles {
  const P = PALETTE.ironwood;
  const rng = new Rng(404);
  const st = ctx.statics;
  const b = BIOMES.ironwood;
  const h = worldHeight;
  const { n, cell, origin } = MAZE;

  // --- maze generation: randomized DFS, then knock out ~15% extra walls for loops ---
  // walls[i][j] = { e: boolean (wall to east), s: boolean (wall to south) }
  const wallE: boolean[][] = [], wallS: boolean[][] = [];
  for (let i = 0; i < n; i++) { wallE.push(new Array(n).fill(true)); wallS.push(new Array(n).fill(true)); }
  const visited: boolean[][] = [];
  for (let i = 0; i < n; i++) visited.push(new Array(n).fill(false));
  const stack: [number, number][] = [[4, 4]];
  visited[4][4] = true;
  while (stack.length) {
    const [i, j] = stack[stack.length - 1];
    const nb: [number, number][] = [];
    if (i > 0 && !visited[i - 1][j]) nb.push([i - 1, j]);
    if (i < n - 1 && !visited[i + 1][j]) nb.push([i + 1, j]);
    if (j > 0 && !visited[i][j - 1]) nb.push([i, j - 1]);
    if (j < n - 1 && !visited[i][j + 1]) nb.push([i, j + 1]);
    if (!nb.length) { stack.pop(); continue; }
    const [ni, nj] = rng.pick(nb);
    if (ni === i + 1) wallE[i][j] = false; else if (ni === i - 1) wallE[ni][nj] = false;
    else if (nj === j + 1) wallS[i][j] = false; else wallS[ni][nj] = false;
    visited[ni][nj] = true;
    stack.push([ni, nj]);
  }
  for (let k = 0; k < 14; k++) {
    const i = rng.int(0, n - 2), j = rng.int(0, n - 2);
    if (rng.chance(0.5)) wallE[i][j] = false; else wallS[i][j] = false;
  }
  // the keep occupies the centre cell (4,4): open all its walls so the keep pad is a clearing
  wallE[3][4] = false; wallE[4][4] = false; wallS[4][3] = false; wallS[4][4] = false;
  // entrance: the corridor arrives from the west at z=0 → cell (0,4); open the west edge (grid edge is open anyway)
  // well cell (4,2) north of the keep: keep it reachable
  wallS[4][2] = false; wallS[4][1] = false;
  // the ravine shortcut: the passage between (1,3) and (1,4) is open but cut by the trench (plank bridges it)
  wallS[1][3] = false;

  const cellCenter = (i: number, j: number) => new Vec3(origin.x + (i + 0.5) * cell, 0, origin.z + (j + 0.5) * cell);
  const blocked = new Set<string>(['1,3-1,4']);
  const open = (i: number, j: number, i2: number, j2: number): boolean => {
    if (i2 < 0 || j2 < 0 || i2 >= n || j2 >= n || i < 0 || j < 0 || i >= n || j >= n) return false;
    if (blocked.has(`${i},${j}-${i2},${j2}`) || blocked.has(`${i2},${j2}-${i},${j}`)) return false;
    if (i2 === i + 1 && j2 === j) return !wallE[i][j];
    if (i2 === i - 1 && j2 === j) return !wallE[i2][j2];
    if (j2 === j + 1 && i2 === i) return !wallS[i][j];
    if (j2 === j - 1 && i2 === i) return !wallS[i2][j2];
    return false;
  };

  // --- connectivity repair: every cell must be reachable from the entrance without the ravine passage ---
  const reachableFrom = (si: number, sj: number): boolean[][] => {
    const seen: boolean[][] = [];
    for (let i = 0; i < n; i++) seen.push(new Array(n).fill(false));
    const q: [number, number][] = [[si, sj]];
    seen[si][sj] = true;
    while (q.length) {
      const [i, j] = q.shift()!;
      for (const [i2, j2] of [[i + 1, j], [i - 1, j], [i, j + 1], [i, j - 1]] as [number, number][]) {
        if (open(i, j, i2, j2) && !seen[i2][j2]) { seen[i2][j2] = true; q.push([i2, j2]); }
      }
    }
    return seen;
  };
  for (let guard = 0; guard < 100; guard++) {
    const seen = reachableFrom(0, 4);
    let fixed = false;
    outer: for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) {
      if (seen[i][j]) continue;
      for (const [i2, j2] of [[i + 1, j], [i - 1, j], [i, j + 1], [i, j - 1]] as [number, number][]) {
        if (i2 < 0 || j2 < 0 || i2 >= n || j2 >= n || !seen[i2][j2]) continue;
        if (blocked.has(`${i},${j}-${i2},${j2}`) || blocked.has(`${i2},${j2}-${i},${j}`)) continue;
        if (i2 === i + 1) wallE[i][j] = false; else if (i2 === i - 1) wallE[i2][j2] = false;
        else if (j2 === j + 1) wallS[i][j] = false; else wallS[i2][j2] = false;
        fixed = true;
        break outer;
      }
    }
    if (!fixed) break;
  }

  // --- trunk clusters along walls ---
  const trunks: Instance[] = [];
  const placeWall = (ax: number, az: number, bx: number, bz: number) => {
    const len = Math.hypot(bx - ax, bz - az);
    const count = Math.max(2, Math.round(len / 2.6));
    for (let k = 0; k < count; k++) {
      const t = (k + 0.5) / count;
      const x = ax + (bx - ax) * t + rng.range(-0.5, 0.5), z = az + (bz - az) * t + rng.range(-0.5, 0.5);
      if (Math.hypot(x - SPOTS.whiteKeep.x, z - SPOTS.whiteKeep.z) < 11) continue;
      if (Math.hypot(x - SPOTS.well.x, z - SPOTS.well.z) < 3) continue;
      const r = rng.range(0.7, 1.1);
      trunks.push({ pos: new Vec3(x, h(x, z) - 0.4, z), yaw: rng.range(0, 6.3), scale: rng.range(0.85, 1.15), tilt: rng.range(-0.03, 0.03) });
      st.addCylinder(x, z, r, h(x, z) - 1, h(x, z) + 12, 'trunk');
    }
  };
  for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) {
    const x0 = origin.x + i * cell, z0 = origin.z + j * cell;
    if (wallE[i][j] && i < n - 1) placeWall(x0 + cell, z0, x0 + cell, z0 + cell);
    if (wallS[i][j] && j < n - 1) placeWall(x0, z0 + cell, x0 + cell, z0 + cell);
  }
  // outer ring of trunks (except the corridor mouth at the west edge, z≈0)
  for (let k = 0; k <= n; k++) {
    const x = origin.x + k * cell, z = origin.z + k * cell;
    placeWall(x, origin.z, x + (k < n ? cell : 0), origin.z);
    placeWall(x, origin.z + n * cell, x + (k < n ? cell : 0), origin.z + n * cell);
    if (Math.abs(z + cell / 2) > 6) placeWall(origin.x, z, origin.x, z + (k < n ? cell : 0));
    placeWall(origin.x + n * cell, z, origin.x + n * cell, z + (k < n ? cell : 0));
  }
  instanced(ctx, 'ironwood.trunks', ironwoodTreeMesh(P[2], [0.06, 0.07, 0.1], 12, 0.8), trunks, { wind: 0.05 });

  // ground mist + canopy: a dark disc plane at 12 m already comes with the trees; add a low mist slab
  const mist = box(b.radius * 2, 0.6, b.radius * 2, [0.2, 0.22, 0.3]);
  setEmissive(mist, [0.1, 0.1, 0.16], 0.4);
  const mt = new Transform(); mt.position.set(b.center.x, 0.5, b.center.z); mt.snapPrev();
  ctx.spawn('ironwood.mist', mt, mist, { transparent: true, alpha: 0.18, castShadow: false, wind: 0.2 });

  // --- three clearings: fallen log (1,1), mushroom ring (7,2), ruined arch (2,7) ---
  const c1 = cellCenter(1, 1), c2 = cellCenter(7, 2), c3 = cellCenter(2, 7);
  // the log lies along the south side of its clearing so the cell-centre corridor stays open
  const lz = c1.z + 3.6;
  const log = rotateZ(cylinder(0.7, 0.8, 7, 8, P[2]), Math.PI / 2);
  translate(log, c1.x, h(c1.x, lz) + 0.7, lz);
  st.addBox(AABB.fromMinMax(c1.x - 3.5, h(c1.x, lz) - 1, lz - 0.8, c1.x + 3.5, h(c1.x, lz) + 1.4, lz + 0.8), 'log');
  const shrooms: Instance[] = [];
  for (let i = 0; i < 9; i++) { const a = (i / 9) * Math.PI * 2; const x = c2.x + Math.cos(a) * 2.6, z = c2.z + Math.sin(a) * 2.6; shrooms.push({ pos: new Vec3(x, h(x, z), z), yaw: a, scale: rng.range(0.8, 1.3) }); }
  instanced(ctx, 'ironwood.shrooms', mushroomMesh([0.75, 0.7, 0.62], [0.9, 0.3, 0.25]), shrooms, { castShadow: false });
  const glow = setEmissive(scaleMesh(sphere(1, 8, 5, [0.9, 0.4, 0.3]), 3, 0.3, 3), [0.9, 0.3, 0.2], 0.5);
  translate(glow, c2.x, h(c2.x, c2.z) + 0.2, c2.z);
  const arch = combine(
    translate(box(1.2, 5, 1.2, P[3]), c3.x - 2.2, h(c3.x, c3.z) + 2.5, c3.z),
    translate(box(1.2, 3.6, 1.2, P[3]), c3.x + 2.2, h(c3.x, c3.z) + 1.8, c3.z),
    translate(rotateX(torusSegment(2.6, 0.5, Math.PI, 12, 6, P[3]), 0), c3.x, h(c3.x, c3.z) + 5, c3.z),
  );
  st.addCylinder(c3.x - 2.2, c3.z, 0.8, -1, 6, 'arch'); st.addCylinder(c3.x + 2.2, c3.z, 0.8, -1, 5, 'arch');
  ctx.spawn('ironwood.clearings', new Transform(), combine(log, glow, arch));

  // --- light shafts: slanted translucent slabs that lean toward the keep ---
  const shafts: ReturnType<typeof box>[] = [];
  const keep = SPOTS.whiteKeep;
  for (let k = 0; k < 14; k++) {
    const a = (k / 14) * Math.PI * 2 + rng.range(-0.2, 0.2), r = rng.range(18, 42);
    const x = keep.x + Math.cos(a) * r, z = keep.z + Math.sin(a) * r;
    const sh = box(1.4, 14, 1.4, [0.9, 0.85, 0.7]);
    setEmissive(sh, [0.9, 0.85, 0.6], 0.35);
    // lean the top toward the keep: shear by rotating about the axis perpendicular to the direction
    const dx = keep.x - x, dz = keep.z - z, l = Math.hypot(dx, dz);
    const lean = 0.35;
    for (let v = 0; v < sh.positions.length; v += 3) {
      const y = sh.positions[v + 1] + 7;
      sh.positions[v] += (dx / l) * y * lean;
      sh.positions[v + 2] += (dz / l) * y * lean;
    }
    translate(sh, x, h(x, z) + 7, z);
    shafts.push(sh);
  }
  ctx.spawn('ironwood.shafts', new Transform(), combine(...shafts), { transparent: true, alpha: 0.16, castShadow: false });

  // --- the well behind the keep ---
  const w = SPOTS.well;
  const wy = h(w.x, w.z);
  const well = combine(
    translate(cylinder(1.3, 1.4, 1.1, 10, P[3]), w.x, wy + 0.55, w.z),
    translate(cylinder(0.9, 0.9, 1.2, 10, [0.02, 0.02, 0.03]), w.x, wy + 0.6, w.z),
    translate(box(0.16, 2.6, 0.16, P[2]), w.x - 1.1, wy + 1.9, w.z),
    translate(box(0.16, 2.6, 0.16, P[2]), w.x + 1.1, wy + 1.9, w.z),
    translate(box(2.8, 0.2, 1.2, P[2]), w.x, wy + 3.2, w.z),
  );
  ctx.spawn('ironwood.well', new Transform(), well);
  st.addCylinder(w.x, w.z, 1.4, wy - 1, wy + 1.1, 'well-rim');

  // fireflies
  let acc = 0;
  const patrol = [cellCenter(1, 4), cellCenter(3, 1), cellCenter(6, 3), cellCenter(7, 6), cellCenter(3, 7), cellCenter(1, 6)];
  const cellOf = (p: Vec3): [number, number] => [Math.floor((p.x - origin.x) / cell), Math.floor((p.z - origin.z) / cell)];
  const path = (a: [number, number], b: [number, number]): Vec3[] => {
    const key = (i: number, j: number) => i + ',' + j;
    const prev = new Map<string, [number, number] | null>();
    const q: [number, number][] = [a];
    prev.set(key(a[0], a[1]), null);
    while (q.length) {
      const [i, j] = q.shift()!;
      if (i === b[0] && j === b[1]) break;
      for (const [i2, j2] of [[i + 1, j], [i - 1, j], [i, j + 1], [i, j - 1]] as [number, number][]) {
        if (!open(i, j, i2, j2) || prev.has(key(i2, j2))) continue;
        prev.set(key(i2, j2), [i, j]);
        q.push([i2, j2]);
      }
    }
    const out: Vec3[] = [];
    let cur: [number, number] | null | undefined = prev.has(key(b[0], b[1])) ? b : null;
    while (cur) { out.push(cellCenter(cur[0], cur[1])); cur = prev.get(key(cur[0], cur[1])); }
    return out.reverse();
  };

  return {
    cellCenter, cellOf, open, path, patrol, blocked, wellPos: new Vec3(w.x, wy, w.z),
    update(playerPos: Vec3, particles: Particles, dt: number): void {
      acc += dt;
      if (acc < 0.25) return;
      acc = 0;
      const x = playerPos.x + rng.range(-16, 16), z = playerPos.z + rng.range(-16, 16);
      particles.mote(_p.set(x, h(x, z) + rng.range(0.6, 2.4), z), _v.set(rng.range(-0.4, 0.4), rng.range(-0.1, 0.3), rng.range(-0.4, 0.4)), rng.range(2, 4), 0.07, [1, 0.9, 0.4], 1.0, 0);
    },
  };
}
const _p = new Vec3(), _v = new Vec3();
